/**
 * Star Rating Display Component
 * Displays star ratings in read-only mode with three display sizes: compact, medium, large
 */

import { StarRatingStats } from '../types/index.js';
import './StarRating.css';

/**
 * Display mode for star ratings
 */
export type StarRatingDisplayMode = 'compact' | 'medium' | 'large';

/**
 * Configuration options for star rating display
 */
export interface StarRatingDisplayConfig {
  /** Display mode */
  mode: StarRatingDisplayMode;
  /** Show rating count */
  showCount?: boolean;
  /** Show distribution bars */
  showDistribution?: boolean;
  /** Custom CSS class */
  className?: string;
  /** Click handler for user rating */
  onRatingClick?: (rating: number) => void;
}

/**
 * StarRatingDisplay - Component for displaying star ratings
 */
export class StarRatingDisplay {
  private container: HTMLElement;
  private stats: StarRatingStats;
  private config: StarRatingDisplayConfig;

  /**
   * Creates a new StarRatingDisplay instance
   * @param stats - Star rating statistics
   * @param config - Display configuration
   */
  constructor(stats: StarRatingStats, config: StarRatingDisplayConfig) {
    this.stats = stats;
    this.config = config;
    this.container = document.createElement('div');
    this.container.className = `komentas-star-rating ${config.mode} ${config.className || ''}`;
    this.render();
  }

  /**
   * Gets the container element
   */
  getElement(): HTMLElement {
    return this.container;
  }

  /**
   * Updates the statistics and re-renders
   */
  update(stats: StarRatingStats): void {
    this.stats = stats;
    this.render();
  }

  /**
   * Renders the star rating display
   */
  private render(): void {
    this.container.innerHTML = '';

    if (this.config.mode === 'compact') {
      this.renderCompact();
    } else if (this.config.mode === 'medium') {
      this.renderMedium();
    } else {
      this.renderLarge();
    }
  }

  /**
   * Renders compact mode - just stars and average
   */
  private renderCompact(): void {
    const wrapper = document.createElement('div');
    wrapper.className = 'star-rating-compact';
    // Add inline styles for compact mode
    wrapper.style.cssText = 'display: inline-flex; align-items: center; gap: 6px; font-size: 13px;';
    console.log('[STAR-DISPLAY] Rendering compact mode, wrapper className:', wrapper.className);

    // Stars
    const starsContainer = document.createElement('div');
    starsContainer.className = 'stars';
    starsContainer.style.cssText = 'display: inline-flex; align-items: center; gap: 1px;';
    starsContainer.innerHTML = this.renderStars(this.stats.average || 0, false);
    wrapper.appendChild(starsContainer);

    // Average and count
    if (this.stats.average !== null) {
      const info = document.createElement('span');
      info.className = 'rating-info';
      info.textContent = `${this.stats.average.toFixed(1)}`;
      if (this.config.showCount && this.stats.totalCount > 0) {
        info.textContent += ` (${this.stats.totalCount})`;
      }
      wrapper.appendChild(info);
    } else if (this.config.showCount) {
      const info = document.createElement('span');
      info.className = 'rating-info no-ratings';
      info.textContent = 'No ratings yet';
      wrapper.appendChild(info);
    }

    this.container.appendChild(wrapper);
  }

  /**
   * Renders medium mode - stars, average, count
   */
  private renderMedium(): void {
    const wrapper = document.createElement('div');
    wrapper.className = 'star-rating-medium';
    // Add inline styles for medium mode with proper styling
    wrapper.style.cssText = 'display: flex; flex-direction: column; gap: 10px; padding: 12px; background: #f9fafb; border-radius: 6px; border: 1px solid #e5e7eb;';
    console.log('[STAR-DISPLAY] Rendering medium mode, wrapper className:', wrapper.className);

    // Stars with average
    const starsSection = document.createElement('div');
    starsSection.className = 'stars-section';
    starsSection.style.cssText = 'display: flex; align-items: center; gap: 8px;';

    const starsContainer = document.createElement('div');
    starsContainer.className = 'stars';
    starsContainer.style.cssText = 'display: flex; align-items: center; gap: 2px;';
    starsContainer.innerHTML = this.renderStars(this.stats.average || 0, true); // true = use large stars
    starsSection.appendChild(starsContainer);

    if (this.stats.average !== null) {
      const averageText = document.createElement('div');
      averageText.className = 'average-text';
      averageText.textContent = `${this.stats.average.toFixed(1)} out of 5`;
      starsSection.appendChild(averageText);
    } else {
      const noRatings = document.createElement('div');
      noRatings.className = 'no-ratings-text';
      noRatings.textContent = 'No ratings yet';
      starsSection.appendChild(noRatings);
    }

    wrapper.appendChild(starsSection);

    // Rating count
    if (this.config.showCount && this.stats.totalCount > 0) {
      const countText = document.createElement('div');
      countText.className = 'count-text';
      countText.textContent = `Based on ${this.stats.totalCount} ${this.stats.totalCount === 1 ? 'rating' : 'ratings'}`;
      wrapper.appendChild(countText);
    }

    this.container.appendChild(wrapper);
  }

  /**
   * Renders large mode - stars, average, count, distribution
   */
  private renderLarge(): void {
    const wrapper = document.createElement('div');
    wrapper.className = 'star-rating-large';

    // Header with stars and average
    const header = document.createElement('div');
    header.className = 'rating-header';

    const starsContainer = document.createElement('div');
    starsContainer.className = 'stars';
    starsContainer.innerHTML = this.renderStars(this.stats.average || 0, true);
    header.appendChild(starsContainer);

    if (this.stats.average !== null) {
      const averageDisplay = document.createElement('div');
      averageDisplay.className = 'average-display';
      averageDisplay.innerHTML = `
        <div class="average-number">${this.stats.average.toFixed(1)}</div>
        <div class="average-label">out of 5</div>
      `;
      header.appendChild(averageDisplay);
    }

    wrapper.appendChild(header);

    // Count
    if (this.config.showCount && this.stats.totalCount > 0) {
      const countText = document.createElement('div');
      countText.className = 'total-count';
      countText.textContent = `${this.stats.totalCount} ${this.stats.totalCount === 1 ? 'rating' : 'ratings'}`;
      wrapper.appendChild(countText);
    }

    // Distribution bars
    if (this.config.showDistribution && this.stats.totalCount > 0) {
      const distribution = document.createElement('div');
      distribution.className = 'rating-distribution';

      for (let stars = 5; stars >= 1; stars--) {
        const count = this.stats.distribution[stars as keyof typeof this.stats.distribution];
        const percentage = this.stats.totalCount > 0 ? (count / this.stats.totalCount) * 100 : 0;

        const row = document.createElement('div');
        row.className = 'distribution-row';

        const label = document.createElement('span');
        label.className = 'distribution-label';
        label.textContent = `${stars} ★`;
        row.appendChild(label);

        const barContainer = document.createElement('div');
        barContainer.className = 'distribution-bar-container';

        const bar = document.createElement('div');
        bar.className = 'distribution-bar';
        bar.style.width = `${percentage}%`;
        barContainer.appendChild(bar);

        row.appendChild(barContainer);

        const countLabel = document.createElement('span');
        countLabel.className = 'distribution-count';
        countLabel.textContent = count.toString();
        row.appendChild(countLabel);

        distribution.appendChild(row);
      }

      wrapper.appendChild(distribution);
    }

    this.container.appendChild(wrapper);
  }

  /**
   * Renders stars HTML
   * @param rating - Rating value (0-5)
   * @param large - Use larger stars
   */
  private renderStars(rating: number, large: boolean): string {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    const starClass = large ? 'star-large' : 'star-small';
    let html = '';

    // Full stars
    for (let i = 0; i < fullStars; i++) {
      html += `<span class="${starClass} star-full" aria-label="Full star">★</span>`;
    }

    // Half star
    if (hasHalfStar) {
      html += `<span class="${starClass} star-half" aria-label="Half star">★</span>`;
    }

    // Empty stars
    for (let i = 0; i < emptyStars; i++) {
      html += `<span class="${starClass} star-empty" aria-label="Empty star">☆</span>`;
    }

    return html;
  }
}
